import React, { useState } from 'react';
import API from '../../../api/API';

/**
 * Elenco e gestione dei gruppi di utenti: quelli che portano permessi e li fanno ereditare ai membri.
 * Non sono i gruppi scout, che stanno nella loro pagina.
 *
 * Il flag bypass e' il superadmin: si puo' mettere solo se lo si ha, altrimenti chi amministra i
 * gruppi si promuoverebbe in due mosse. Il gruppo di sistema non si tocca.
 */
const vuoto = { nome: '', descrizione: '', bypass: false };

const TabGruppi = ({ gruppi, utenti, me, onCambiato, onApriPermessi }) => {
  const [form, setForm] = useState(null);
  const [membri, setMembri] = useState(null);
  const [errori, setErrori] = useState([]);
  const [salvando, setSalvando] = useState(false);

  const mostraErrori = (res) => {
    const dati = res.data;
    setErrori(Array.isArray(dati) ? dati : [res.errorMsg || 'Operazione non riuscita']);
  };

  const apriNuovo = () => { setErrori([]); setMembri(null); setForm({ ...vuoto, modo: 'nuovo' }); };

  const apriModifica = (g) => {
    setErrori([]);
    setMembri(null);
    setForm({ modo: 'modifica', id: g.id, nome: g.nome, descrizione: g.descrizione || '', bypass: g.bypass });
  };

  const apriMembri = (g) => {
    setErrori([]);
    setForm(null);
    setMembri({ id: g.id, nome: g.nome, ids: (g.membri || []).map(m => m.id) });
  };

  const chiudi = () => { setForm(null); setMembri(null); setErrori([]); };

  const salva = async () => {
    setSalvando(true);
    setErrori([]);

    const corpo = { nome: form.nome, descrizione: form.descrizione || null, bypass: form.bypass };
    const res = form.modo === 'nuovo'
      ? await API.gruppiUtenti.crea({ ...corpo, permessi: [] })
      : await API.gruppiUtenti.aggiorna(form.id, corpo);

    setSalvando(false);

    if (!res.isSuccess) { mostraErrori(res); return; }

    chiudi();
    await onCambiato();
  };

  const salvaMembri = async () => {
    setSalvando(true);
    setErrori([]);

    const res = await API.gruppiUtenti.impostaMembri(membri.id, membri.ids);
    setSalvando(false);

    if (!res.isSuccess) { mostraErrori(res); return; }

    chiudi();
    await onCambiato();
  };

  const cancella = async (g) => {
    if (!window.confirm(`Cancellare il gruppo "${g.nome}"? Spariscono i suoi permessi e le appartenenze.`)) return;

    const res = await API.gruppiUtenti.cancella(g.id);
    if (!res.isSuccess) {
      const dati = res.data;
      alert(Array.isArray(dati) ? dati.join('\n') : (res.errorMsg || 'Cancellazione non riuscita'));
      return;
    }
    await onCambiato();
  };

  const puoCreare = me?.bypass || me?.globale?.GRUPPO_UTENTI === 'SCRITTURA'
    || (me?.gruppiTornei || []).some(a => a.permessi?.GRUPPO_UTENTI === 'SCRITTURA')
    || (me?.edizioni || []).some(a => a.permessi?.GRUPPO_UTENTI === 'SCRITTURA');

  return (
    <>
      <div className="utenti-barra">
        <p className="utenti-nota">
          Un gruppo porta permessi: chi ne fa parte li eredita, e sulla sua riga puo' sempre
          sovrascriverli con un valore proprio.
        </p>
        <button className="btn btn-primary" onClick={apriNuovo} disabled={!puoCreare}
                title={puoCreare ? 'Crea un gruppo' : 'Ti serve la scrittura su GRUPPO_UTENTI in almeno un ambito'}>
          <i className="fas fa-plus"></i> Nuovo gruppo
        </button>
      </div>

      {form && (
        <div className="utenti-form">
          <h3>{form.modo === 'nuovo' ? 'Nuovo gruppo' : `Modifica di ${form.nome}`}</h3>

          <div className="utenti-form-griglia">
            <label>
              Nome
              <input value={form.nome} onChange={e => setForm({ ...form, nome: e.target.value })} />
            </label>
            <label className="utenti-form-larga">
              Descrizione
              <input value={form.descrizione} onChange={e => setForm({ ...form, descrizione: e.target.value })} />
            </label>
            <label className="utenti-form-check">
              <input type="checkbox" checked={form.bypass} disabled={!me?.bypass}
                     title={me?.bypass ? 'Accesso completo per i membri' : 'Solo chi ha il bypass puo\' assegnarlo'}
                     onChange={e => setForm({ ...form, bypass: e.target.checked })} />
              Bypass (accesso completo)
            </label>
          </div>

          {errori.length > 0 && (
            <ul className="utenti-errore utenti-errore-lista">
              {errori.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          )}

          <div className="utenti-form-azioni">
            <button className="btn btn-primary" onClick={salva} disabled={salvando}>
              {salvando ? 'Salvataggio...' : 'Salva'}
            </button>
            <button className="btn btn-secondary" onClick={chiudi} disabled={salvando}>Annulla</button>
          </div>
        </div>
      )}

      {membri && (
        <div className="utenti-form">
          <h3>Membri di {membri.nome}</h3>
          <p className="utenti-nota">
            Entrare in un gruppo vuol dire prendersi tutta la sua matrice, quindi puoi aggiungere solo
            utenti che sei in grado di amministrare, e non puoi cambiare la tua appartenenza.
          </p>

          <div className="utenti-form-gruppi">
            {utenti.map(u => (
              <label key={u.id} className="utenti-chip">
                <input
                  type="checkbox"
                  checked={membri.ids.includes(u.id)}
                  disabled={!u.gestibile || u.id === me?.id}
                  title={u.id === me?.id ? 'Non puoi cambiare la tua appartenenza'
                                         : (u.gestibile ? '' : 'Ha permessi che tu non hai')}
                  onChange={e => setMembri({
                    ...membri,
                    ids: e.target.checked
                      ? [...membri.ids, u.id]
                      : membri.ids.filter(x => x !== u.id)
                  })}
                />
                {u.user}
              </label>
            ))}
          </div>

          {errori.length > 0 && (
            <ul className="utenti-errore utenti-errore-lista">
              {errori.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          )}

          <div className="utenti-form-azioni">
            <button className="btn btn-primary" onClick={salvaMembri} disabled={salvando}>
              {salvando ? 'Salvataggio...' : 'Salva'}
            </button>
            <button className="btn btn-secondary" onClick={chiudi} disabled={salvando}>Annulla</button>
          </div>
        </div>
      )}

      <div className="utenti-tabella-wrap">
        <table className="utenti-tabella">
          <thead>
            <tr>
              <th>Nome</th>
              <th>Descrizione</th>
              <th>Membri</th>
              <th>Celle</th>
              <th>Azioni</th>
            </tr>
          </thead>
          <tbody>
            {gruppi.map(g => (
              <tr key={g.id}>
                <td>
                  <strong>{g.nome}</strong>
                  {g.bypass && <span className="utenti-tag utenti-tag-bypass">bypass</span>}
                  {g.sistema && <span className="utenti-tag">sistema</span>}
                </td>
                <td>{g.descrizione}</td>
                <td>
                  {(g.membri || []).length === 0
                    ? <span className="utenti-nota">nessuno</span>
                    : g.membri.map(m => <span key={m.id} className="utenti-tag">{m.user}</span>)}
                </td>
                <td>{g.celle}</td>
                <td className="utenti-azioni">
                  <button className="btn btn-info btn-sm" title="Permessi"
                          onClick={() => onApriPermessi(g.id, g.nome)}>
                    <i className="fas fa-table"></i>
                  </button>
                  <button className="btn btn-success btn-sm" title="Membri"
                          disabled={g.sistema && !me?.bypass} onClick={() => apriMembri(g)}>
                    <i className="fas fa-user-plus"></i>
                  </button>
                  <button className="btn btn-secondary btn-sm"
                          title={g.sistema ? 'Gruppo di sistema' : (g.gestibile ? 'Modifica' : 'Ha permessi che tu non hai')}
                          disabled={g.sistema || !g.gestibile} onClick={() => apriModifica(g)}>
                    <i className="fas fa-pen"></i>
                  </button>
                  <button className="btn btn-delete btn-sm"
                          title={g.sistema ? 'Gruppo di sistema' : (g.gestibile ? 'Cancella' : 'Ha permessi che tu non hai')}
                          disabled={g.sistema || !g.gestibile} onClick={() => cancella(g)}>
                    <i className="fas fa-trash"></i>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default TabGruppi;
